/**
 * FounderUpgradeDialog — pre-checkout interstitial for Founder purchases.
 *
 * Founder: when a user with an active Enferno sub buys Founder,
 * we DON'T silently swallow their remaining sub time. This modal:
 *   - Fetches GET /api/payments/founder/upgrade-info
 *   - If no active sub: lets them confirm and continue to Stripe
 *   - If active sub: shows remaining value + asks how to handle it:
 *       (a) Convert remaining value → Enferno merch store credit
 *           (issued when merch launches)
 *       (b) Cancel + acknowledge no refund (effectively a small extra
 *           donation; some users will pick this)
 *
 * The choice is sent to POST /api/payments/checkout/founder which:
 *   - Stamps the choice + remaining value on the payment_transactions
 *     record
 *   - Cancels the prior subscription on the webhook (no proration)
 *   - Stores merch_credit_due_usd for future Enferno store reconciliation
 *
 * Rendered via portal-to-body so it stacks above any other modals.
 */
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Crown, Loader2, AlertCircle, Gift, Heart } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FounderUpgradeDialog = ({ onClose, onConfirm }) => {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [choice, setChoice] = useState('');  // 'credit' | 'forfeit'
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/payments/founder/upgrade-info`)
      .then((r) => { if (alive) setInfo(r.data); })
      .catch((e) => {
        if (alive) setError(typeof e?.response?.data?.detail === 'string'
          ? e.response.data.detail
          : 'Could not load upgrade info. Try again.');
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const hasActive = info?.has_active_subscription;
  const canSubmit = !hasActive || !!choice;

  const handleConfirm = async () => {
    setSubmitting(true);
    setError('');
    try {
      await onConfirm(hasActive ? choice : null);
      // Parent navigates to Stripe; we don't reset submitting (page unloads).
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : detail?.message || 'Checkout failed. Try again.');
      setSubmitting(false);
    }
  };

  const dialog = (
    <div className="fixed inset-0 z-[70] bg-black/80 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="founder-upgrade-dialog">
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-lg max-h-[92vh] overflow-y-auto bg-enf-surface-deep border border-amber-700/40 rounded-xl shadow-2xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-amber-700/40 bg-gradient-to-br from-amber-900/30 to-transparent">
          <div className="flex items-center gap-2">
            <Crown className="w-4 h-4 text-amber-400" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] font-semibold text-amber-400">Becoming a Founder</div>
              <div className="font-display text-xl tracking-wide leading-none mt-1 text-enf-text-primary">$249 · lifetime access</div>
            </div>
          </div>
          <button type="button" onClick={onClose} className="p-2 hover:bg-amber-900/30 rounded-lg" data-testid="founder-upgrade-close">
            <X className="w-4 h-4 text-enf-text-muted" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {loading && (
            <div className="flex items-center gap-2 text-sm text-enf-text-secondary">
              <Loader2 className="w-4 h-4 animate-spin" /> Checking your subscription…
            </div>
          )}

          {!loading && info && !hasActive && (
            <div className="space-y-3 text-sm text-enf-text-secondary">
              <p>You're about to lock in lifetime Enferno access for a one-time $249.</p>
              <ul className="space-y-1.5 text-sm">
                <li className="flex items-start gap-2"><Crown className="w-3.5 h-3.5 text-amber-400 mt-0.5 flex-shrink-0" /><span>Numbered Founder seat on the Donors Wall</span></li>
                <li className="flex items-start gap-2"><Gift className="w-3.5 h-3.5 text-amber-400 mt-0.5 flex-shrink-0" /><span>Numbered tee + sticker pack mailed at launch</span></li>
                <li className="flex items-start gap-2"><Heart className="w-3.5 h-3.5 text-amber-400 mt-0.5 flex-shrink-0" /><span>10% off all Enferno merch, forever</span></li>
              </ul>
            </div>
          )}

          {!loading && hasActive && (
            <div className="space-y-4">
              <div className="px-4 py-3 rounded-lg bg-enf-surface-mid border border-enf-surface-border" data-testid="founder-upgrade-active-sub-summary">
                <div className="text-[10px] uppercase tracking-widest font-semibold text-enf-text-muted mb-1">Your current subscription</div>
                <div className="text-sm text-enf-text-primary">
                  {info.subscription_plan === 'enferno-monthly' ? 'Enferno (Monthly)' : info.subscription_plan === 'enferno-annual' ? 'Enferno (Annual)' : 'Enferno'}
                </div>
                <div className="text-xs text-enf-text-secondary mt-1">
                  {info.remaining_days} day{info.remaining_days === 1 ? '' : 's'} remaining (~${info.remaining_value_usd.toFixed(2)} of paid time left)
                </div>
              </div>

              <div>
                <p className="text-sm text-enf-text-secondary mb-3">
                  Founder lifetime overrides your current subscription. We won't pocket the remaining time — you pick how to handle it:
                </p>
                <div className="space-y-2">
                  <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${choice === 'credit' ? 'bg-amber-900/20 border-amber-700/60' : 'bg-enf-surface-mid border-enf-surface-border hover:border-amber-700/30'}`} data-testid="founder-upgrade-choice-credit">
                    <input type="radio" name="merch-credit-choice" value="credit" checked={choice === 'credit'} onChange={(e) => setChoice(e.target.value)} className="mt-1" />
                    <div className="flex-1">
                      <div className="text-sm font-semibold text-enf-text-primary flex items-center gap-2">
                        <Gift className="w-3.5 h-3.5 text-amber-400" /> Convert to Enferno merch credit
                      </div>
                      <p className="text-xs text-enf-text-secondary mt-1">
                        ${info.remaining_value_usd.toFixed(2)} stored as store credit, applied automatically at Enferno store launch. Stacks with your 10% Founder discount.
                      </p>
                    </div>
                  </label>
                  <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${choice === 'forfeit' ? 'bg-amber-900/20 border-amber-700/60' : 'bg-enf-surface-mid border-enf-surface-border hover:border-amber-700/30'}`} data-testid="founder-upgrade-choice-forfeit">
                    <input type="radio" name="merch-credit-choice" value="forfeit" checked={choice === 'forfeit'} onChange={(e) => setChoice(e.target.value)} className="mt-1" />
                    <div className="flex-1">
                      <div className="text-sm font-semibold text-enf-text-primary flex items-center gap-2">
                        <Heart className="w-3.5 h-3.5 text-amber-400" /> Donate in support of Enferno's mission
                      </div>
                      <p className="text-xs text-enf-text-secondary mt-1">
                        No store credit. Treat the remaining ${info.remaining_value_usd.toFixed(2)} as an extra contribution to the mission. Quiet support.
                      </p>
                    </div>
                  </label>
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="px-3 py-2 bg-enf-role-danger/10 border border-enf-role-danger/40 rounded text-xs text-enf-accent-fg flex items-center gap-2" data-testid="founder-upgrade-error">
              <AlertCircle className="w-3 h-3" /> {error}
            </div>
          )}
        </div>

        <div className="px-5 py-4 border-t border-amber-700/40 bg-enf-surface-deep flex items-center gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-enf-text-secondary hover:text-enf-text-primary" data-testid="founder-upgrade-cancel">
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={loading || submitting || !canSubmit}
            className="flex-1 px-5 py-2.5 bg-amber-600 hover:bg-amber-500 text-zinc-950 rounded-md font-semibold text-sm flex items-center justify-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="founder-upgrade-continue"
          >
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            {submitting ? 'Opening checkout…' : 'Continue to Stripe checkout'}
          </button>
        </div>
      </div>
    </div>
  );

  return typeof document !== 'undefined' ? createPortal(dialog, document.body) : dialog;
};

export default FounderUpgradeDialog;
